// Painel de Performance - Replicando Sentry Frontend Insights

import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Activity, AlertTriangle, CheckCircle2, Cloud, History } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useSentryPerformance, useUpdateSentryIssues } from '@/hooks/useSentryPerformance';
import type { PeriodFilter, WebVitalKey } from '@/types/performance';

// Components
import { 
  PerformanceScoreDonut,
  StatsCard,
  ThroughputChart,
  DurationChart,
  FrequentIssues,
  TransactionsTable,
  WebVitalCard,
  PagesTableSentry,
  SentryNotConfigured,
  ErrorsListSentry,
} from '@/components/admin/performance';

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: '24h', label: 'Últimas 24 horas' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
];

const WEB_VITAL_KEYS: WebVitalKey[] = ['lcp', 'fcp', 'inp', 'cls', 'ttfb'];

export default function PerformanceDashboard() {
  const [period, setPeriod] = useState<PeriodFilter>('7d');
  const { data: sentryData, isLoading } = useSentryPerformance({ period, issueStatus: 'unresolved' });
  const { data: sentryIssueBoardData, isLoading: isBoardLoading } = useSentryPerformance({ period, issueStatus: 'all' });
  const updateIssues = useUpdateSentryIssues({ period });
  const unresolvedIssueCount = sentryData?.stats.unresolvedIssueCount ?? 0;
  const readyToResolveCount = sentryData?.stats.readyToResolveCount ?? 0;
  const mostRecentIssue = sentryData?.errors?.reduce((latest, current) => {
    if (!latest) return current;
    return new Date(current.lastSeen).getTime() > new Date(latest.lastSeen).getTime() ? current : latest;
  }, sentryData?.errors?.[0]);
  const snapshotLabel = sentryData?.fetchedAt
    ? new Date(sentryData.fetchedAt).toLocaleString('pt-BR')
    : null;

  // If Sentry is not configured
  if (!isLoading && !sentryData) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Painel de Performance
          </h1>
        </div>
        <SentryNotConfigured />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Painel de Performance
          </h1>
          <Badge className="bg-purple-600 hover:bg-purple-700 gap-1.5">
            <Cloud className="h-3 w-3" />
            Sentry
          </Badge>
        </div>

        <Select value={period} onValueChange={(v) => setPeriod(v as PeriodFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="webvitals">Web Vitals</TabsTrigger>
          <TabsTrigger value="errors">Erros</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <StatsCard
              title="Issues abertas"
              value={unresolvedIssueCount}
              icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
              description="Status operacional atual no Sentry"
              variant={unresolvedIssueCount > 0 ? 'danger' : 'success'}
              isLoading={isLoading}
            />
            <StatsCard
              title="Prontas para resolver"
              value={readyToResolveCount}
              icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
              description="Sem evento novo nas últimas 24h"
              variant="success"
              isLoading={isLoading}
            />
            <StatsCard
              title="Último evento"
              value={mostRecentIssue ? new Date(mostRecentIssue.lastSeen).toLocaleDateString('pt-BR') : '-'}
              icon={<History className="h-5 w-5 text-slate-600" />}
              description={snapshotLabel ? `Snapshot atualizado em ${snapshotLabel}` : 'Sem snapshot recente'}
              isLoading={isLoading}
            />
          </div>

          {/* Row 1: Charts */}
          <div className="grid gap-4 lg:grid-cols-3">
            <ThroughputChart 
              data={sentryData?.timeSeries.throughput || []} 
              isLoading={isLoading} 
            />
            <DurationChart 
              data={sentryData?.timeSeries.duration || []} 
              isLoading={isLoading} 
            />
            <FrequentIssues 
              errors={sentryData?.errors || []} 
              isLoading={isLoading} 
            />
          </div>

          {/* Row 2: Score Donut */}
          <div className="grid gap-4 lg:grid-cols-4">
            <PerformanceScoreDonut 
              score={sentryData?.performanceScore || 0}
              metricScores={sentryData?.metricScores || { lcp: 50, fcp: 50, cls: 50, fid: 50, inp: 50, ttfb: 50 }}
              isLoading={isLoading}
            />
            <div className="lg:col-span-3">
              <TransactionsTable 
                transactions={sentryData?.transactions || []} 
                isLoading={isLoading} 
              />
            </div>
          </div>
        </TabsContent>

        {/* Web Vitals Tab */}
        <TabsContent value="webvitals" className="space-y-6">
          {/* Score + Cards */}
          <div className="grid gap-4 lg:grid-cols-6">
            <div className="lg:col-span-2">
              <PerformanceScoreDonut 
                score={sentryData?.performanceScore || 0}
                metricScores={sentryData?.metricScores || { lcp: 50, fcp: 50, cls: 50, fid: 50, inp: 50, ttfb: 50 }}
                isLoading={isLoading}
              />
            </div>
            <div className="lg:col-span-4 grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              {WEB_VITAL_KEYS.map((key) => (
                <WebVitalCard 
                  key={key}
                  metricKey={key} 
                  value={sentryData?.webVitals[key] ?? null}
                  score={sentryData?.metricScores[key] ?? 50}
                  isLoading={isLoading} 
                />
              ))}
            </div>
          </div>

          {/* Pages Table */}
          <PagesTableSentry 
            pages={sentryData?.pages || []} 
            isLoading={isLoading} 
          />
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors" className="space-y-4">
          <Card className="border-dashed bg-muted/20">
            <CardContent className="p-4 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Painel operacional de issues</p>
              <p className="mt-1">
                O kanban abaixo separa o que ainda precisa de correcao, o que esta em monitoramento, o que ja pode ser fechado no Sentry e o que ja foi tratado.
                Cada acao gera feedback imediato e registra historico interno.
              </p>
            </CardContent>
          </Card>
          <ErrorsListSentry
            errors={sentryIssueBoardData?.errors || []}
            isLoading={isBoardLoading}
            isUpdating={updateIssues.isPending}
            onAction={({ issueIds, status, issueTitles }) =>
              updateIssues.mutateAsync({ issueIds, status, issueTitles })
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
